/**
 * EQuIP Dashboard Query Hooks
 *
 * React Query hooks for dashboard endpoints: timeline, leadership aggregation,
 * award history, audit detail, and award comparison.
 * Wraps equipApi.dashboard.* with typed key factories and enabled guards
 * for conditional parameters.
 */

import { useQuery } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import type {
  QuarterTimelineConfig,
  LeadershipAggregation,
  SeniorLeadershipAggregation,
  AwardHistoryItem,
  AuditDetail,
  LeaderMeasureFacility,
  LeaderMeasurePatientsResponse,
  LeaderAcoPatientsResponse,
  LeaderAuditClinician,
  IncentiveProjection,
} from '@/lib/equip-types'

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const equipDashboardKeys = {
  all: () => ['equip', 'dashboard'] as const,
  availableQuarters: () => ['equip', 'dashboard', 'available-quarters'] as const,
  timeline: (year: number, quarter: number) =>
    ['equip', 'dashboard', 'timeline', year, quarter] as const,
  leadershipAggregation: (year: number, quarter: number, managerEmail?: string) =>
    ['equip', 'dashboard', 'leadership-aggregation', year, quarter, managerEmail ?? 'all'] as const,
  awardHistory: (npi: string) =>
    ['equip', 'dashboard', 'award-history', npi] as const,
  auditDetail: (auditId: number, auditType: string) =>
    ['equip', 'dashboard', 'audit-detail', auditId, auditType] as const,
  awardComparison: (npi: string, year: number, quarter: number, oldVer: number, newVer: number) =>
    ['equip', 'dashboard', 'award-comparison', npi, year, quarter, oldVer, newVer] as const,
  leaderMeasureFacilities: (leaderEmail: string, measureName: string, year: number, quarter: number) =>
    ['equip', 'dashboard', 'leader-measure-facilities', leaderEmail, measureName, year, quarter] as const,
  leaderMeasurePatients: (leaderEmail: string, measureName: string, year: number, quarter: number) =>
    ['equip', 'dashboard', 'leader-measure-patients', leaderEmail, measureName, year, quarter] as const,
  leaderAuditClinicians: (leaderEmail: string, auditType: string, year: number, quarter: number) =>
    ['equip', 'dashboard', 'leader-audit-clinicians', leaderEmail, auditType, year, quarter] as const,
  leaderAcoPatients: (year: number, quarter: number) =>
    ['equip', 'dashboard', 'leader-aco-patients', year, quarter] as const,
  seniorLeadershipAggregation: (year: number, quarter: number, seniorLeadEmail?: string) =>
    ['equip', 'dashboard', 'senior-leadership-aggregation', year, quarter, seniorLeadEmail ?? 'self'] as const,
  regionalIncentive: (year: number, quarter: number, seniorLeadEmail?: string) =>
    ['equip', 'dashboard', 'regional-incentive', year, quarter, seniorLeadEmail ?? 'self'] as const,
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches available quarters from equip_config_snapshot for the quarter selector.
 * Cached aggressively — quarter configs rarely change.
 */
export function useAvailableQuarters() {
  return useQuery<{ year: number; quarter: number; status: string }[]>({
    queryKey: equipDashboardKeys.availableQuarters(),
    queryFn: () => equipApi.dashboard.getAvailableQuarters(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetches the quarter timeline configuration for a given year/quarter.
 * No enabled guard needed — year/quarter are always valid numbers.
 */
export function useTimeline(year: number, quarter: number) {
  return useQuery<QuarterTimelineConfig>({
    queryKey: equipDashboardKeys.timeline(year, quarter),
    queryFn: () => equipApi.dashboard.getTimeline({ year, quarter }),
  })
}

/**
 * Fetches the leadership aggregation data for a given year/quarter.
 * Optionally scoped to a specific manager's team via managerEmail.
 * No enabled guard needed — year/quarter are always valid numbers.
 */
export function useLeadershipAggregation(
  year: number,
  quarter: number,
  managerEmail?: string
) {
  return useQuery<LeadershipAggregation>({
    queryKey: equipDashboardKeys.leadershipAggregation(year, quarter, managerEmail),
    queryFn: () =>
      equipApi.dashboard.getLeadershipAggregation({ year, quarter, managerEmail }),
  })
}

/**
 * Fetches the senior leadership aggregation data for a given year/quarter.
 * Provides a region-level rollup: one row per ADCOE, with their team summaries.
 * Optionally scoped to a specific senior leader via seniorLeadEmail (admin only).
 */
export function useSeniorLeadershipAggregation(
  year: number,
  quarter: number,
  seniorLeadEmail?: string,
) {
  return useQuery<SeniorLeadershipAggregation>({
    queryKey: equipDashboardKeys.seniorLeadershipAggregation(year, quarter, seniorLeadEmail),
    queryFn: () =>
      equipApi.dashboard.getSeniorLeadershipAggregation({ year, quarter, seniorLeadEmail }),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetches the RDCOE regional incentive projection for a given year/quarter.
 * Mirrors the ADCOE-level leader incentive projection one level up the org
 * hierarchy. Optionally scoped to a specific senior leader via
 * seniorLeadEmail (admin only). Disabled when `enabled` is false — used by
 * callers to skip the fetch when dollar values are hidden (canSeeAwards).
 */
export function useRegionalIncentive(
  year: number,
  quarter: number,
  seniorLeadEmail?: string,
  enabled: boolean = true,
) {
  return useQuery<IncentiveProjection>({
    queryKey: equipDashboardKeys.regionalIncentive(year, quarter, seniorLeadEmail),
    queryFn: () =>
      equipApi.dashboard.getRegionalIncentive({ year, quarter, seniorLeadEmail }),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetches the award history for a given provider NPI.
 * Query is disabled when npi is null/undefined — no request fires.
 */
export function useAwardHistory(npi: string | null | undefined) {
  return useQuery<AwardHistoryItem[]>({
    queryKey: equipDashboardKeys.awardHistory(npi ?? ''),
    queryFn: () => equipApi.dashboard.getAwardHistory(npi!),
    enabled: !!npi,
  })
}

/**
 * Fetches detailed audit information for a specific audit.
 * Query is disabled when auditId is null — no request fires.
 */
export function useAuditDetail(auditId: number | null, auditType: string) {
  return useQuery<AuditDetail>({
    queryKey: equipDashboardKeys.auditDetail(auditId ?? 0, auditType),
    queryFn: () => equipApi.dashboard.getAuditDetail(auditId!, auditType),
    enabled: auditId !== null,
  })
}

/**
 * Fetches facility-level breakdown for a leader's measure performance.
 * Disabled when leaderEmail or measureName is null.
 */
export function useLeaderMeasureFacilities(
  leaderEmail: string | null,
  measureName: string | null,
  year: number,
  quarter: number
) {
  return useQuery<LeaderMeasureFacility[]>({
    queryKey: equipDashboardKeys.leaderMeasureFacilities(leaderEmail ?? '', measureName ?? '', year, quarter),
    queryFn: () => equipApi.dashboard.getLeaderMeasureFacilities(leaderEmail!, measureName!, year, quarter),
    enabled: !!leaderEmail && !!measureName,
  })
}

/**
 * Fetches a flat patient list for a leader's measure performance across all facilities.
 * Disabled when leaderEmail or measureName is null.
 */
export function useLeaderMeasurePatients(
  leaderEmail: string | null,
  measureName: string | null,
  year: number,
  quarter: number
) {
  return useQuery<LeaderMeasurePatientsResponse>({
    queryKey: equipDashboardKeys.leaderMeasurePatients(leaderEmail ?? '', measureName ?? '', year, quarter),
    queryFn: () => equipApi.dashboard.getLeaderMeasurePatients(leaderEmail!, measureName!, year, quarter),
    enabled: !!leaderEmail && !!measureName,
  })
}

/**
 * Fetches all ACO patients in a leader's territory with per-measure
 * eligible/met flags for the whole ACO (org-wide).
 */
export function useLeaderAcoPatients(year: number, quarter: number) {
  return useQuery<LeaderAcoPatientsResponse>({
    queryKey: equipDashboardKeys.leaderAcoPatients(year, quarter),
    queryFn: () => equipApi.dashboard.getLeaderAcoPatients({ year, quarter }),
    enabled: year > 0,
  })
}

/**
 * Fetches clinician-level audit summary under a leader for a given audit type.
 * Disabled when leaderEmail or auditType is null.
 */
export function useLeaderAuditClinicians(
  leaderEmail: string | null,
  auditType: string | null,
  year: number,
  quarter: number
) {
  return useQuery<LeaderAuditClinician[]>({
    queryKey: equipDashboardKeys.leaderAuditClinicians(leaderEmail ?? '', auditType ?? '', year, quarter),
    queryFn: () => equipApi.dashboard.getLeaderAuditClinicians(leaderEmail!, auditType!, year, quarter),
    enabled: !!leaderEmail && !!auditType,
  })
}

/**
 * Fetches award comparison data between two snapshot versions.
 * Uses a raw fetch — this endpoint is not exposed via equipApi service.
 */
export function useAwardComparison(
  npi: string,
  year: number,
  quarter: number,
  oldVersion: number,
  newVersion: number
) {
  return useQuery<unknown>({
    queryKey: equipDashboardKeys.awardComparison(npi, year, quarter, oldVersion, newVersion),
    queryFn: async () => {
      const API_BASE_URL = import.meta.env.VITE_API_URL || ''
      const params = new URLSearchParams({
        year: String(year),
        quarter: String(quarter),
        old_version: String(oldVersion),
        new_version: String(newVersion),
      })
      const res = await fetch(
        `${API_BASE_URL}/api/equip/overrides/comparison/${encodeURIComponent(npi)}?${params}`,
        { credentials: 'include' }
      )
      if (!res.ok) throw new Error('Failed to fetch comparison data')
      return res.json()
    },
  })
}
